import { Injectable, inject } from '@angular/core';

import { environment } from '../../environments/environment';
import { DateRangeFilter } from '../models/common.models';
import {
  CaseUsageEntry,
  CategoryStockBreakdown,
  ReportSummaryStats,
  StockMovementEntry,
} from '../models/report.models';
import { InventoryItem } from '../models/inventory.models';
import {
  filterByDateRange,
  filterSnapshotByDateRange,
} from '../utils/date-range.util';
import { EquipmentService } from './equipment.service';
import { InventoryService } from './inventory.service';
import { DashboardService } from './dashboard.service';

@Injectable({ providedIn: 'root' })
export class ReportService {
  private readonly inventory = inject(InventoryService);
  private readonly equipment = inject(EquipmentService);
  private readonly dashboard = inject(DashboardService);

  getStockOnHandSummary(range?: DateRangeFilter): ReportSummaryStats {
    const items = this.getStockOnHandItems(range);
    const locations = new Set(
      this.inventory.getStockSnapshots().map((row) => row.location),
    );
    const categories = new Set(items.map((item) => item.category));
    const totalValue = items.reduce(
      (sum, item) => sum + item.stockLevel * item.price,
      0,
    );

    return {
      totalItems: items.length,
      totalValue,
      categories: categories.size,
      locations: locations.size,
    };
  }

  getStockOnHandByCategory(range?: DateRangeFilter): CategoryStockBreakdown[] {
    const grouped = new Map<string, CategoryStockBreakdown>();

    for (const item of this.getStockOnHandItems(range)) {
      const existing = grouped.get(item.category) ?? {
        category: item.category,
        quantity: 0,
        value: 0,
      };

      existing.quantity += item.stockLevel;
      existing.value += item.stockLevel * item.price;
      grouped.set(item.category, existing);
    }

    return [...grouped.values()].sort((a, b) => b.value - a.value);
  }

  getLowStockReport(range?: DateRangeFilter): InventoryItem[] {
    const items = this.inventory.lowStockItems();
    return filterSnapshotByDateRange(items, range, (row) => row.lastUpdated);
  }

  getStockMovement(range?: DateRangeFilter): StockMovementEntry[] {
    const issues = this.inventory.getStockIssues();
    const received = this.inventory.getGoodsReceived();
    const transfers = this.inventory.getStockTransfers();

    const movements: StockMovementEntry[] = [
      ...received.map((note) => ({
        id: note.id,
        date: note.receivedDate,
        itemCode: '—',
        itemName: `${note.items} items`,
        movementType: 'received' as const,
        quantity: note.items,
        location: 'Warehouse A',
        reference: note.grnNumber,
      })),
      ...issues.map((issue) => ({
        id: issue.id,
        date: issue.issueDate,
        itemCode: '—',
        itemName: `${issue.items} items`,
        movementType: 'issued' as const,
        quantity: issue.items,
        location: issue.branch,
        reference: issue.issueNumber,
      })),
      ...transfers.map((transfer) => ({
        id: transfer.id,
        date: transfer.transferDate,
        itemCode: '—',
        itemName: `${transfer.items} items`,
        movementType: 'transferred' as const,
        quantity: transfer.items,
        location: `${transfer.fromLocation} → ${transfer.toLocation}`,
        reference: transfer.transferNumber,
      })),
    ];

    return filterByDateRange(
      movements.sort((a, b) => b.date.localeCompare(a.date)),
      range,
      (row) => row.date,
    );
  }

  getCaseUsage(range?: DateRangeFilter): CaseUsageEntry[] {
    let entries: CaseUsageEntry[];

    if (!environment.useMockData) {
      const caseUsage = this.dashboard.caseUsage();
      entries = caseUsage.length ? caseUsage : [];
    } else {
      entries = [
        {
          id: '1',
          caseRef: 'JOB-2026-0142',
          date: '2026-06-20',
          branch: 'Sandton',
          itemsIssued: 12,
          totalValue: 4850,
          caseType: 'Event',
        },
        {
          id: '2',
          caseRef: 'ORD-2026-0143',
          date: '2026-06-22',
          branch: 'Pretoria',
          itemsIssued: 8,
          totalValue: 3120,
          caseType: 'Internal',
        },
      ];
    }

    return filterByDateRange(entries, range, (row) => row.date);
  }

  /** @deprecated Use getCaseUsage */
  getFuneralUsage(range?: DateRangeFilter): CaseUsageEntry[] {
    return this.getCaseUsage(range);
  }

  getCheckedOutEquipmentReport(range?: DateRangeFilter) {
    const rows = this.equipment
      .getCheckouts()
      .filter((row) => row.status === 'active' || row.status === 'overdue');

    return filterByDateRange(rows, range, (row) => row.checkoutDate);
  }

  getOverdueReturnsReport(range?: DateRangeFilter) {
    return filterByDateRange(
      this.equipment.getOverdueCheckouts(),
      range,
      (row) => row.dueDate,
    );
  }

  getDamagedEquipmentReport(range?: DateRangeFilter) {
    return filterByDateRange(
      this.equipment.getDamagedRecords(),
      range,
      (row) => row.reportedDate,
    );
  }

  getStockValuation(range?: DateRangeFilter) {
    return this.getStockOnHandItems(range).map((item) => ({
      itemCode: item.code,
      itemName: item.name,
      category: item.category,
      quantity: item.stockLevel,
      unitCost: item.price,
      totalValue: item.stockLevel * item.price,
    }));
  }

  private getStockOnHandItems(range?: DateRangeFilter): InventoryItem[] {
    return filterSnapshotByDateRange(
      this.inventory.allItems(),
      range,
      (row) => row.lastUpdated,
    );
  }
}
